import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Button,
  Alert,
} from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import * as FaIcons from "react-icons/fa";
import { useDocumentProcessor } from "../common/useDocumentProcessor";
import firebaseApp from "../../../firebase/credenciales";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  getFirestore,
  updateDoc,
  doc,
  orderBy,
} from "firebase/firestore";
import axios from "axios"; // Añadido para llamadas a backend
import { FUNCTION_URLS } from "../../../config/functionUrls";

const db = getFirestore(firebaseApp);

// Utility to convert file to base64
const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]); // Keep only the data
    reader.onerror = (error) => reject(error);
  });
};

const HiroCVProcessor = ({ ruc }) => {
  const [pdfJsLoaded, setPdfJsLoaded] = useState(false);
  const [requiredPosition, setRequiredPosition] = useState("");
  const [linkedVacanteId, setLinkedVacanteId] = useState("");
  const [linkedProcesoId, setLinkedProcesoId] = useState("");
  const [linkedVacanteTitle, setLinkedVacanteTitle] = useState("");
  const [vacantesDisponibles, setVacantesDisponibles] = useState([]);
  const [latestRanking, setLatestRanking] = useState([]);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [retryEmbeddingsLoading, setRetryEmbeddingsLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 }); // Progreso para masiva
  const [bulkCandidates, setBulkCandidates] = useState([]); // Array de {file, name} para masiva

  const {
    processing,
    setProcessing,
    statusMessage,
    setStatusMessage,
    error,
    setError,
    success,
    setSuccess,
    uploadToFirebase,
    indexFragmentsToFirestore,
    showNotification,
  } = useDocumentProcessor("hiro");

  const HIRO_EVALUAR_URL = FUNCTION_URLS.hiroEvaluarCandidatos;

  // Cargar PDF.js
  useEffect(() => {
    const loadPdfJs = async () => {
      try {
        const pdfjsLib = await import("pdfjs-dist");
        pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
        window.pdfjsLib = pdfjsLib;
        setPdfJsLoaded(true);
      } catch (error) {
        setError(`Error cargando PDF.js: ${error.message}`);
      }
    };
    loadPdfJs();
  }, []);

  useEffect(() => {
    const loadVacantes = async () => {
      try {
        if (!ruc) return;
        const q = query(
          collection(db, "rs_vacantes"),
          where("ruc", "==", ruc),
          where("estado", "==", "activa"),
          orderBy("fechaCreacion", "desc")
        );
        const snapshot = await getDocs(q);
        const vacantes = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setVacantesDisponibles(vacantes);
      } catch (err) {
        console.error("Error cargando vacantes disponibles para Hiro:", err);
      }
    };
    loadVacantes();
  }, [ruc]);

  useEffect(() => {
    const initializeRecruitingContext = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const vacanteId = params.get("vacanteId") || "";
        const procesoIdParam = params.get("procesoId") || "";
        if (!vacanteId) return;

        setLinkedVacanteId(vacanteId);
        if (procesoIdParam) setLinkedProcesoId(procesoIdParam);

        const vacanteRef = doc(db, "rs_vacantes", vacanteId);
        const vacanteSnap = await getDoc(vacanteRef);
        if (vacanteSnap.exists()) {
          const vacanteData = vacanteSnap.data();
          setLinkedVacanteTitle(vacanteData.titulo || "");
          if (!requiredPosition && vacanteData.titulo) setRequiredPosition(vacanteData.titulo);
          if (!procesoIdParam && vacanteData.hiroProcesoId) {
            setLinkedProcesoId(vacanteData.hiroProcesoId);
          }
        }
      } catch (err) {
        console.error("Error cargando contexto de reclutamiento en HiroCVProcessor:", err);
      }
    };
    initializeRecruitingContext();
  }, []);

  const handleVacanteLinkChange = async (vacanteId) => {
    try {
      setLinkedVacanteId(vacanteId || "");
      if (!vacanteId) {
        setLinkedProcesoId("");
        setLinkedVacanteTitle("");
        return;
      }
      const vacanteRef = doc(db, "rs_vacantes", vacanteId);
      const vacanteSnap = await getDoc(vacanteRef);
      if (!vacanteSnap.exists()) return;
      const vacanteData = vacanteSnap.data();
      setLinkedVacanteTitle(vacanteData.titulo || "");
      setLinkedProcesoId(vacanteData.hiroProcesoId || "");
      if (vacanteData.titulo) setRequiredPosition(vacanteData.titulo);
    } catch (err) {
      console.error("Error vinculando vacante en HiroCVProcessor:", err);
    }
  };

  // Manejar selección de archivos (masiva) - Extraer nombres y mostrar lista
  const handleBulkFilesChange = (event) => {
    const files = Array.from(event.target.files);
    const candidates = files.map((file, index) => {
      const fileName = file.name.replace(/\.(pdf|jpe?g|png)$/i, "");
      let candidateName = fileName
        .replace(/cv|curriculum|vitae|resume|_|-/gi, "")
        .trim();

      // Logging para depuración
      console.log(`Archivo ${index + 1}: ${file.name} -> Nombre extraído: "${candidateName}"`);

      if (candidateName.length < 3) {
        candidateName = `Candidato ${index + 1}`; // Fallback
      }

      return { file, name: candidateName };
    });

    setBulkCandidates(candidates);
    setError(null);
    setSuccess(false);
  };

  // Función para actualizar nombre en masivo
  const updateBulkCandidateName = (index, newName) => {
    const updated = [...bulkCandidates];
    updated[index].name = newName.trim();
    setBulkCandidates(updated);
  };

  // Extraer texto completo del PDF o procesar Imagen
  const extractFullText = async (file, candidateName) => {
    let fullText = "";
    let imageBase64 = null;
    let mimeType = null;
    let pageCount = 1;

    if (file.type.startsWith("image/")) {
      imageBase64 = await convertToBase64(file);
      mimeType = file.type;
      fullText = "[Imagen adjunta para análisis OCR y Detección facial]";
    } else {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument({
        data: new Uint8Array(arrayBuffer),
      }).promise;
      
      pageCount = pdf.numPages;
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = extractPageText(textContent);
        fullText += pageText + "\n\n";
      }
    }

    const documentMetadata = {
      title: `Documento - ${candidateName}`,
      description: `Agente Hiro: Documento de ${candidateName} para posición: ${requiredPosition}`,
      pageCount: pageCount,
      extractionDate: new Date().toISOString(),
      documentId: uuidv4().substring(0, 8),
      agent_type: "hiro",
      sourceFilename: file.name,
      ruc: ruc,
      candidate_name: candidateName.trim(),
      required_position: requiredPosition,
      seniority_level: "mid",
      telefono: null,
      vacanteId: linkedVacanteId || null,
      procesoId: linkedProcesoId || null,
      document_type: "cv",
      category: "cv",
      status: "active",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    return {
      fullText,
      imageBase64,
      mimeType,
      pageCount,
      metadata: documentMetadata,
    };
  };

  // Extraer texto de página
  const extractPageText = (textContent) => {
    let lastY = -1;
    let text = "";
    const textItems = textContent.items.sort((a, b) => {
      if (Math.abs(a.transform[5] - b.transform[5]) < 5)
        return a.transform[4] - b.transform[4];
      return b.transform[5] - a.transform[5];
    });

    for (const item of textItems) {
      const currentY = item.transform[5];
      if (lastY !== -1 && Math.abs(currentY - lastY) > 5) text += "\n";
      if (text.length > 0 && !text.endsWith(" ") && !item.str.startsWith(" "))
        text += " ";
      text += item.str;
      lastY = currentY;
    }

    return text
      .replace(/\s+\n/g, "\n")
      .replace(/\n\s+/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  // Función para resumir CV con Gemini (llamada al backend)
  const summarizeCVWithGemini = async (fullText, imageBase64, mimeType, candidateName) => {
    try {
      const payload = {
        text: fullText,
        candidateName,
        ruc,
      };

      if (imageBase64 && mimeType) {
        payload.imageBase64 = imageBase64;
        payload.mimeType = mimeType;
      }

      const response = await axios.post(
        FUNCTION_URLS.hiroResumirCv,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      return response.data.summary; // Devuelve el resumen estructurado
    } catch (error) {
      throw new Error(`Error resumiendo CV: ${error.message}`);
    }
  };

  // Función para procesar embedding único del resumen
  const processSummaryEmbedding = async (summary, fullText, documentId, metadata) => {
    try {
      const response = await axios.post(
        FUNCTION_URLS.hiroProcessSummaryEmbeddingRun,
        {
          summary,
          fullText,
          documentId,
          metadata,
          ruc,
          telefono: metadata?.telefono || null,
          procesoId: linkedProcesoId || null,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(`Error procesando embedding: ${error.message}`);
    }
  };

  const triggerHiroEvaluation = async () => {
    if (!linkedVacanteId || !ruc) return null;
    try {
      setRankingLoading(true);
      setStatusMessage("Actualizando ranking de candidatos con Hiro...");
      const response = await axios.post(
        HIRO_EVALUAR_URL,
        {
          vacanteId: linkedVacanteId,
          ruc,
          maxResults: 10,
          procesoId: linkedProcesoId || null,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const data = response.data || {};
      const top5 = Array.isArray(data.candidatos) ? data.candidatos.slice(0, 5) : [];
      setLatestRanking(top5);

      if (linkedVacanteId) {
        await updateDoc(doc(db, "rs_vacantes", linkedVacanteId), {
          hiroRankingTop5: top5,
          hiroLastEvaluationAt: new Date().toISOString(),
          hiroLastEvaluationStatus: "ok",
          ...(data.procesoId ? { hiroProcesoId: data.procesoId } : {}),
        });
      }
      if (!linkedProcesoId && data.procesoId) {
        setLinkedProcesoId(data.procesoId);
      }
      return data;
    } catch (error) {
      console.error("Error actualizando ranking Hiro:", error);
      if (linkedVacanteId) {
        await updateDoc(doc(db, "rs_vacantes", linkedVacanteId), {
          hiroLastEvaluationAt: new Date().toISOString(),
          hiroLastEvaluationStatus: "error",
        });
      }
      throw error;
    } finally {
      setRankingLoading(false);
    }
  };

  // Proceso masivo
  const handleBulkProcess = async () => {
    if (bulkCandidates.length === 0 || !ruc) {
      setError("Se requieren archivos y RUC");
      return;
    }

    if (!pdfJsLoaded) {
      setError("PDF.js no se ha cargado aún.");
      return;
    }

    try {
      setProcessing(true);
      setBulkProgress({ current: 0, total: bulkCandidates.length });
      setError(null);
      setSuccess(false);

      for (let i = 0; i < bulkCandidates.length; i++) {
        const { file, name: candidateName } = bulkCandidates[i];
        setBulkProgress({ current: i + 1, total: bulkCandidates.length });

        console.log(`Procesando ${i + 1}/${bulkCandidates.length}: ${candidateName}`); // Logging

        setStatusMessage(`Procesando CV ${i + 1}/${bulkCandidates.length}: ${candidateName}`);

        // Subir archivo
        const uploadResult = await uploadToFirebase(file, ruc);

        // Extraer texto o imagen
        const extractResult = await extractFullText(file, candidateName);

        // Generar resumen
        const summary = await summarizeCVWithGemini(
          extractResult.fullText,
          extractResult.imageBase64,
          extractResult.mimeType,
          candidateName
        );

        // Procesar embedding
        await processSummaryEmbedding(
          summary, 
          summary.fullText || extractResult.fullText, 
          extractResult.metadata.documentId, 
          extractResult.metadata
        );

        // Indexar documento (sin fragmentos)
        await indexFragmentsToFirestore(
          uploadResult.storagePath,
          [], // Sin fragmentos
          { ...extractResult.metadata, summary }
        );
      }

      setStatusMessage("¡Todos los CVs procesados!");
      setSuccess(true);

      setBulkCandidates([]);
      setBulkProgress({ current: 0, total: 0 });

      showNotification(
        "¡CVs procesados!",
        `${bulkCandidates.length} CVs han sido procesados correctamente. Ahora puedes generar/recalcular ranking manualmente.`,
        "success"
      );
    } catch (error) {
      console.error("Error en subida masiva:", error);
      setError(`Error: ${error.message}`);
      showNotification("Error", error.message, "error");
    } finally {
      setProcessing(false);
    }
  };

  const retryPendingEmbeddingsForVacante = async () => {
    if (!linkedVacanteId || !ruc) {
      setError("Seleccione una vacante para reintentar embeddings pendientes.");
      return;
    }

    try {
      setRetryEmbeddingsLoading(true);
      setError(null);
      setStatusMessage("Buscando documentos de la vacante con embeddings pendientes...");

      const fragmentosRef = collection(db, "hiro_fragmentos");
      const [legacySnap, currentSnap] = await Promise.all([
        getDocs(query(fragmentosRef, where("ruc", "==", ruc), where("vacante_id", "==", linkedVacanteId))),
        getDocs(query(fragmentosRef, where("ruc", "==", ruc), where("vacanteId", "==", linkedVacanteId))),
      ]);

      const documentIds = new Set();
      [legacySnap, currentSnap].forEach((snap) => {
        snap.forEach((d) => {
          const data = d.data() || {};
          if (data.document_id) documentIds.add(data.document_id);
        });
      });

      if (documentIds.size === 0) {
        showNotification(
          "Sin pendientes",
          "No se encontraron documentos vinculados a esta vacante para reintentar embeddings.",
          "info"
        );
        return;
      }

      let processedTotal = 0;
      let quotaBlocked = false;
      let docsProcessed = 0;
      const endpoint = FUNCTION_URLS.hiroProcessFragmentEmbeddings;

      for (const documentId of documentIds) {
        let keepProcessingDoc = true;
        let docIterations = 0;

        while (keepProcessingDoc && docIterations < 20) {
          docIterations++;
          const response = await axios.post(endpoint, {
            documentId,
            batchSize: 20,
            ruc,
          });

          const data = response.data || {};
          if (data.code === "EMBEDDING_QUOTA_EXCEEDED") {
            quotaBlocked = true;
            keepProcessingDoc = false;
            break;
          }

          if (data.success && (data.processed || 0) > 0) {
            processedTotal += data.processed;
            setStatusMessage(
              `Reprocesando embeddings pendientes... ${processedTotal} fragmentos actualizados`
            );
          } else {
            keepProcessingDoc = false;
          }
        }

        docsProcessed++;
        setStatusMessage(
          `Reprocesando embeddings pendientes... documentos ${docsProcessed}/${documentIds.size}`
        );

        if (quotaBlocked) break;
      }

      if (quotaBlocked) {
        showNotification(
          "Cuota temporal alcanzada",
          `Se procesaron ${processedTotal} fragmentos antes de agotar cuota. Reintenta en unos minutos.`,
          "warning"
        );
      } else {
        showNotification(
          "Embeddings reintentados",
          `Proceso completado. Fragmentos procesados en este intento: ${processedTotal}.`,
          "success"
        );
      }

      if (!quotaBlocked) {
        await triggerHiroEvaluation();
      }
    } catch (error) {
      console.error("Error reintentando embeddings pendientes:", error);
      setError(`Error reintentando embeddings pendientes: ${error.message}`);
      showNotification("Error", error.message, "error");
    } finally {
      setRetryEmbeddingsLoading(false);
    }
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >

          {/* Header Premium - mismo estilo que Lexi */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
              borderBottom: "3px solid #D2691E",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaIcons.FaUserTie className="me-3" />
                Hiro - Procesador de CVs
              </h2>
              <p className="mb-0 opacity-75">
                Análisis inteligente de talento para matching y reclutamiento
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>

            {/* Tarjeta de subida */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">

                <Form>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Vincular a Vacante (recomendado)</Form.Label>
                        <Form.Select
                          value={linkedVacanteId}
                          onChange={(e) => handleVacanteLinkChange(e.target.value)}
                          disabled={processing}
                        >
                          <option value="">Sin vacante vinculada</option>
                          {vacantesDisponibles.map((vacante) => (
                            <option key={vacante.id} value={vacante.id}>
                              {vacante.titulo || "Vacante"}{vacante.area ? ` - ${vacante.area}` : ""}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Al vincular la vacante, los CVs se registran dentro del proceso de Hiro para ranking automático.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  {(linkedVacanteId || linkedProcesoId) && (
                    <Alert variant="info" className="mb-3">
                      <div><strong>Vacante vinculada:</strong> {linkedVacanteTitle || linkedVacanteId}</div>
                      <div><strong>Proceso Hiro:</strong> {linkedProcesoId || "Pendiente"}</div>
                    </Alert>
                  )}
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Seleccionar CVs (Múltiple)</Form.Label>
                        <Form.Control
                          type="file"
                          multiple
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={handleBulkFilesChange}
                          disabled={processing}
                        />
                        {bulkCandidates.length > 0 && (
                          <div className="mt-3 p-3 bg-white rounded shadow-sm">
                            <p className="fw-bold mb-2">{bulkCandidates.length} archivos seleccionados:</p>
                            <Row>
                              {bulkCandidates.map((candidate, index) => (
                                <Col md={6} key={index} className="mb-2">
                                  <small className="text-muted d-block text-truncate">{candidate.file.name}</small>
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={candidate.name}
                                    onChange={(e) => updateBulkCandidateName(index, e.target.value)}
                                    disabled={processing}
                                    placeholder="Nombre del candidato"
                                  />
                                </Col>
                              ))}
                            </Row>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <div>
                    {bulkProgress.total > 0 && (
                      <Alert variant="info">
                        Procesando {bulkProgress.current} de {bulkProgress.total} CVs...
                      </Alert>
                    )}
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="primary"
                        onClick={handleBulkProcess}
                        disabled={processing || rankingLoading || bulkCandidates.length === 0 || !linkedVacanteId}
                      >
                        <FaIcons.FaCloudUploadAlt className="me-2" />
                        Procesar CVs
                      </Button>
                      <Button
                        variant="success"
                        onClick={triggerHiroEvaluation}
                        disabled={processing || rankingLoading || retryEmbeddingsLoading || !linkedVacanteId}
                      >
                        <FaIcons.FaListOl className="me-2" />
                        {rankingLoading ? "Generando ranking..." : "Generar / Recalcular ranking"}
                      </Button>
                      <Button
                        variant="warning"
                        onClick={retryPendingEmbeddingsForVacante}
                        disabled={processing || rankingLoading || retryEmbeddingsLoading || !linkedVacanteId}
                      >
                        <FaIcons.FaRedo className="me-2" />
                        {retryEmbeddingsLoading
                          ? "Reintentando embeddings..."
                          : "Reintentar embeddings pendientes"}
                      </Button>
                    </div>
                    {!linkedVacanteId && (
                      <Form.Text className="text-danger d-block mt-2">
                        Debe seleccionar una vacante para procesar CVs.
                      </Form.Text>
                    )}
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {/* Lista de CVs Procesados */}
            <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
              <Card.Header
                className="text-white py-3"
                style={{
                  background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <div className="d-flex align-items-center">
                  <FaIcons.FaDatabase className="me-2" />
                  <span className="fw-semibold">Candidatos Procesados (Firestore Vector Search)</span>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <p className="text-center text-muted">
                  Los candidatos procesados están disponibles para consultas semánticas via el Chat de Hiro.
                </p>
                {latestRanking.length > 0 && (
                  <div className="mt-3">
                    <h6 className="fw-bold">Top 5 Ranking (Hiro)</h6>
                    <ol className="mb-0">
                      {latestRanking.map((c, idx) => (
                        <li key={c.candidatoId || idx} className="mb-1">
                          {(c.nombre || "Sin nombre")} - Score: {c.scoreFinal ?? c.evaluacion?.puntuacion ?? 0}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </Card.Body>
            </Card>

          </Card.Body>
        </Card>

      </Container>
    </div>
  );
};

export default HiroCVProcessor;
